import { useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { createColumnHelper, type SortingState } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { exportHref } from "@jbnl/api-client";
import { Alert, AlertList } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";
import {
  DataTable,
  DataTableSkeleton,
  dataTableFeatures,
} from "@/components/ui/data-table";
import type { Filterdescriptor } from "@/components/ui/data-table-filter";
import { Pager } from "@/components/ui/pager";
import { ListDetail } from "@/components/ListDetail";
import { FULL_PAGE_SIZE, toNumber } from "@/lib/paging";
import { useFilterStand } from "@/lib/useFilterStand";
import { datumKort } from "@/features/wedstrijden/datum";
import {
  useWedstrijddagen,
  type Seizoen,
} from "@/features/wedstrijden/useWedstrijden";
import {
  alsReistijd,
  useOndersteunerReistijdenVoorDag,
  useOndersteunersZonderPunt,
  type OndersteunerReistijd,
} from "./useReistijden";
import { REDENEN, zonderPuntNaam } from "./zonderPunt";

/** Every `OndersteunerPunt` name, `Gevonden` included - the filter's own list, wider than
 * `REDENEN` since this one is about the routepunt rather than about the gap in it. */
const PUNTEN = ["Gevonden", ...REDENEN] as const;

/** The `sortBy` value the API understands. `naam` is the server's own default in either direction. */
const SORT_BY: Record<string, string> = { reistijd: "seconds" };

/** A date sorts by its ISO form, not by how it is written - see the korps screen's own note. */
const opDatum = (
  a: { original: { date: string } },
  b: { original: { date: string } },
) => a.original.date.localeCompare(b.original.date);

const dagKolommen = (t: TFunction<["paginas", "reistijden"]>) =>
  createColumnHelper<
    typeof dataTableFeatures,
    {
      id: string;
      date: string;
      organiserendKorpsName: string;
      niveauName: string;
    }
  >().columns([
    {
      id: "datum",
      header: t("reistijden:dagen.datum"),
      accessorFn: (dag) => datumKort(dag.date),
      sortFn: opDatum,
      cell: (info) => (
        <span className="whitespace-nowrap">{info.getValue()}</span>
      ),
    },
    {
      id: "korps",
      header: t("reistijden:dagen.korps"),
      accessorKey: "organiserendKorpsName",
    },
    {
      id: "niveau",
      header: t("reistijden:dagen.niveau"),
      accessorKey: "niveauName",
    },
  ]);

const helper = createColumnHelper<
  typeof dataTableFeatures,
  OndersteunerReistijd
>();

/** One group per reason, because the three send a beheerder somewhere different. A name per line,
 * because a fresh season can put a hundred people here and a run-on line is not one you can keep
 * your place in. */
function ZonderPuntWaarschuwing({
  t,
  zonderPunt,
}: {
  t: TFunction<["paginas", "reistijden"]>;
  zonderPunt: NonNullable<ReturnType<typeof useOndersteunersZonderPunt>["data"]>;
}) {
  return (
    // `warning` and not `danger`: nobody is blocked and nobody is disadvantaged - the
    // planning simply drops the term for these people and ranks them on functierotatie alone.
    // What there is to do is upstream, in VBS or Portaal, and only a person can do it.
    <AlertList
      tone="warning"
      className="mb-6"
      title={t("paginas:reistijdenOndersteuners.zonderPunt.titel", {
        count: zonderPunt.length,
      })}
      description={t("paginas:reistijdenOndersteuners.zonderPunt.uitleg")}
    >
      {REDENEN.filter((reden) =>
        zonderPunt.some((rij) => rij.reden === reden),
      ).map((reden) => {
        const rijen = zonderPunt.filter((rij) => rij.reden === reden);

        return (
          <div key={reden} className="mt-3">
            <p>
              {t(
                `paginas:reistijdenOndersteuners.zonderPunt.${reden}` as "paginas:reistijdenOndersteuners.zonderPunt.GeenPostcode",
                { count: rijen.length },
              )}
            </p>
            <ul className="mt-2 list-disc space-y-0.5 ps-5 text-muted-foreground">
              {rijen.map((rij) => (
                <li key={rij.persoonId}>{zonderPuntNaam(t, reden, rij)}</li>
              ))}
            </ul>
          </div>
        );
      })}
    </AlertList>
  );
}

/**
 * A beheerder comes for the gap, not the numbers: this is a tiebreaker weighed under
 * functierotatie. Read-only, because an override is keyed by coordinate pair and would correct
 * it for whoever else lives at that point - the fix an ondersteuner needs is in VBS (ADR-0053).
 */
export function OndersteunerReistijden({
  seizoen,
  dagId,
  onKies,
  pagina,
  onPaginaChange,
}: {
  seizoen: Seizoen;
  dagId: string | undefined;
  onKies: (id?: string) => void;
  pagina: number | undefined;
  onPaginaChange: (pagina: number) => void;
}) {
  const { t } = useTranslation(["paginas", "reistijden"]);
  const { data: alleDagen, isPending } = useWedstrijddagen(seizoen.id);
  const { data: zonderPunt } = useOndersteunersZonderPunt();

  // The same narrowing the korps screen applies, and for the same reason: reistijd is measured to
  // the meldadres, so a dag whose host has not been appointed has no point to route to and would
  // sit here permanently empty.
  const dagen = alleDagen?.filter(
    (dag): dag is typeof dag & { organiserendKorpsName: string } =>
      dag.organiserendKorpsName !== null &&
      dag.organiserendKorpsName !== undefined,
  );

  const dag = dagen?.find((d) => d.id === dagId);

  return (
    <>
      {zonderPunt && zonderPunt.length > 0 && (
        <ZonderPuntWaarschuwing t={t} zonderPunt={zonderPunt} />
      )}

      <ListDetail
        selected={Boolean(dag)}
        backLabel={t("reistijden:dagen.terug")}
        onBack={() => onKies()}
        list={
          <Card>
            <CardContent className="pt-6">
              {isPending ? (
                <DataTableSkeleton
                  label={t("reistijden:dagen.label")}
                  columns={dagKolommen(t)}
                  rows={6}
                  search
                />
              ) : (
                // No `filters` here either - see the korps screen's own note on why.
                <DataTable
                  label={t("reistijden:dagen.label")}
                  data={dagen ?? []}
                  columns={dagKolommen(t)}
                  getRowId={(row) => row.id}
                  selectedId={dag?.id}
                  onSelect={onKies}
                  searchPlaceholder={t("reistijden:dagen.zoek")}
                  empty={t("reistijden:dagen.leeg")}
                  clientPageSize={FULL_PAGE_SIZE}
                />
              )}
            </CardContent>
          </Card>
        }
        detail={
          dag ? (
            <VoorDag
              key={dag.id}
              wedstrijddagId={dag.id}
              pagina={pagina}
              onPaginaChange={onPaginaChange}
            />
          ) : (
            <Card>
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground">
                  {t("paginas:reistijdenOndersteuners.tabel.kies")}
                </p>
              </CardContent>
            </Card>
          )
        }
      />
    </>
  );
}

/**
 * Three states, never two. A persoon with no point at all is not "not computed yet" - nothing is
 * coming for them - and neither is ever a zero or a dash, which would read as living next door
 * (ADR-0053).
 */
function ReistijdCel({
  rij,
  t,
}: {
  rij: OndersteunerReistijd;
  t: TFunction<"paginas">;
}) {
  if (rij.seconds != null) {
    return <span className="font-medium">{alsReistijd(rij.seconds)}</span>;
  }

  return (
    <span className="text-muted-foreground">
      {rij.punt === "Gevonden"
        ? t("reistijdenOndersteuners.tabel.nietBerekend")
        : t(
            `reistijdenOndersteuners.tabel.${rij.punt}` as "reistijdenOndersteuners.tabel.GeenPostcode",
          )}
    </span>
  );
}

function VoorDag({
  wedstrijddagId,
  pagina,
  onPaginaChange,
}: {
  wedstrijddagId: string;
  pagina: number | undefined;
  onPaginaChange: (pagina: number) => void;
}) {
  const { t } = useTranslation("paginas");
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const sort = sorting[0];

  // Server-driven, like the korps screen's own bron filter: no `past`, so no counts either.
  const descriptors = useMemo<Filterdescriptor<OndersteunerReistijd>[]>(
    () => [
      {
        id: "punt",
        label: t("reistijdenOndersteuners.tabel.filter.label"),
        kind: "meerkeuze",
        opties: PUNTEN.map((punt) => ({
          waarde: punt,
          label: t(
            `reistijdenOndersteuners.tabel.filter.${punt}` as "reistijdenOndersteuners.tabel.filter.Gevonden",
          ),
        })),
      },
    ],
    [t],
  );
  const { stand, onStand } = useFilterStand(descriptors, "/reistijden/ondersteuners");
  const punt = stand.punt?.soort === "meerkeuze" ? stand.punt.waarden : undefined;

  const { data: result, isPending } = useOndersteunerReistijdenVoorDag(
    wedstrijddagId,
    pagina,
    FULL_PAGE_SIZE,
    search,
    sort && SORT_BY[sort.id],
    sort?.desc ?? false,
    punt,
  );

  const kolommen = helper.columns([
    helper.accessor("naam", {
      header: t("reistijdenOndersteuners.tabel.naam"),
    }),
    helper.accessor((rij) => rij.seconds, {
      id: "reistijd",
      header: t("reistijdenOndersteuners.tabel.reistijd"),
      cell: (info) => <ReistijdCel rij={info.row.original} t={t} />,
    }),
  ]);

  return (
    <Card>
      <CardContent className="grid gap-4 pt-6">
        {/* Information, not a warning: this resolves itself on the next background run. Describes
          the whole dag, not the page below. */}
        {result && toNumber(result.nietBerekendCount) > 0 && (
          <Alert tone="info">
            {t("reistijdenOndersteuners.tabel.nietBerekendCount", {
              count: toNumber(result.nietBerekendCount),
            })}
          </Alert>
        )}

        {isPending || !result ? (
          <DataTableSkeleton
            label={t("reistijdenOndersteuners.tabel.label")}
            columns={kolommen}
            rows={6}
            search
          />
        ) : (
          <>
            {/* No onSelect: there is nothing to open. An override is keyed by coordinate pair,
                so it belongs to the point rather than to the person sitting on it. */}
            <DataTable
              label={t("reistijdenOndersteuners.tabel.label")}
              data={result.page.items}
              columns={kolommen}
              getRowId={(row) => `${row.persoonId}-${row.wedstrijddagId}`}
              searchPlaceholder={t("reistijdenOndersteuners.tabel.zoek")}
              empty={t("reistijdenOndersteuners.tabel.leeg")}
              exportHref={exportHref(
                "/api/beheer/wedstrijddagen/{wedstrijddagId}/ondersteuner-reistijden/export",
                {
                  path: { wedstrijddagId },
                  query: {
                    search,
                    sortBy: sort && SORT_BY[sort.id],
                    sortDescending: sort?.desc ?? false,
                    punt,
                  },
                },
              )}
              serverDriven={{
                sorting,
                onSortingChange: setSorting,
                globalFilter: search,
                // A search narrows the set the server counts, so page 2 of the old set would
                // otherwise land past the end of the new, shorter one.
                onGlobalFilterChange: (value) => {
                  setSearch(value);
                  onPaginaChange(1);
                },
              }}
              filters={{
                descriptors,
                stand,
                onStand,
                // No counts: this table is `serverDriven`, so nothing here can count what it has
                // not fetched - see the map's decision on that.
                toonTelling: false,
              }}
            />
            <Pager
              page={result.page.page}
              pageSize={result.page.pageSize}
              totalCount={result.page.totalCount}
              onPageChange={onPaginaChange}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
